import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ToastContext, type ToastEntry, type ToastInput } from './toast-context';
import { P, ghost } from '../palette';
import { sans } from '../typography';
import { Ico, L } from '../icons';
import { tokens } from '../tokens';
import { motion } from '../motion';

export interface ToastProps {
  message: string;
  detail?: string;
  level: 'success' | 'error' | 'warning' | 'info' | 'neutral';
  action?: { label: string; onClick: () => void };
  onDismiss: () => void;
}

const levelMap = {
  success: { color: P.pos, ghostRef: ghost.pos, icon: L.checkCircle },
  error: { color: P.neg, ghostRef: ghost.neg, icon: L.alertCircle },
  warning: { color: P.warn, ghostRef: ghost.warn, icon: L.alertTri },
  info: { color: P.info, ghostRef: ghost.info, icon: L.info },
  neutral: { color: P.t2, ghostRef: ghost.muted, icon: L.info },
} as const;

export function Toast({ message, detail, level, action, onDismiss }: ToastProps) {
  const m = levelMap[level];
  const duration = tokens.motion.toastDismiss[level];
  const [progress, setProgress] = useState(100);
  const startRef = useRef(0);
  const rafRef = useRef(0);

  useEffect(() => {
    if (duration === 0) return;
    startRef.current = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startRef.current;
      const pct = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(pct);
      if (pct <= 0) {
        onDismiss();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [duration, onDismiss]);

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '12px 14px',
        background: P.s2,
        border: `1px solid ${P.border}`,
        borderRadius: 8,
        maxWidth: 360,
        width: '100%',
        overflow: 'hidden',
        animation: `cp-slide-in-bottom ${motion.duration.normal} ${motion.easing.decelerate}`,
        willChange: 'transform, opacity',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: m.color,
          borderRadius: '8px 0 0 8px',
        }}
      />
      <div style={{ flexShrink: 0, marginLeft: 4, marginTop: 1 }}>
        <Ico paths={[...m.icon]} size={18} color={m.color} sw={2} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: sans,
            fontSize: 13,
            fontWeight: 700,
            color: P.t1,
            lineHeight: 1.3,
          }}
        >
          {message}
        </div>
        {detail && (
          <div
            style={{
              fontFamily: sans,
              fontSize: 11,
              fontWeight: 400,
              color: P.t2,
              lineHeight: 1.4,
              marginTop: 3,
            }}
          >
            {detail}
          </div>
        )}
        {action && (
          <div style={{ marginTop: 6 }}>
            <span
              onClick={action.onClick}
              style={{
                fontFamily: sans,
                fontSize: 11,
                fontWeight: 700,
                color: P.accent,
                cursor: 'pointer',
              }}
            >
              {action.label}
            </span>
          </div>
        )}
      </div>
      {duration !== 0 && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2 }}>
          <div
            style={{
              height: '100%',
              width: `${progress}%`,
              background: m.color,
              opacity: 0.4,
              borderRadius: '0 1px 0 0',
            }}
          />
        </div>
      )}
    </div>
  );
}

export function ToastContainer({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const counterRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((props: ToastInput) => {
    const id = ++counterRef.current;
    setToasts((prev) => [{ ...props, id }, ...prev].slice(0, 4));
  }, []);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {toasts.length > 0 &&
        createPortal(
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              bottom: isMobile ? 72 : 24,
              right: isMobile ? 0 : 24,
              left: isMobile ? 0 : undefined,
              zIndex: 2000,
              display: 'flex',
              flexDirection: 'column',
              alignItems: isMobile ? 'center' : 'flex-end',
              gap: 8,
              padding: isMobile ? '0 12px' : 0,
            }}
          >
            {toasts.map((t) => (
              <Toast
                key={t.id}
                message={t.message}
                detail={t.detail}
                level={t.level}
                action={t.action}
                onDismiss={() => dismiss(t.id)}
              />
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}
